// @ts-nocheck
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import constants from "@store/constants";
import { getShipmentStatusesDataView } from "@store/Framework/Store/Greif/Data/ShipmentStatuses/ShipmentStatusesSelectors";
import updateSearchFields from "@store/Framework/Store/Greif/Pages/ShipmentHistory/Handlers/UpdateSearchFields";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { tableFilterProps } from "@store/Themes/common/props/table";

const mapStateToProps = (state: ApplicationState) => ({
    parameter: state.greif.pages.shipmentHistory.parameter,
    shipmentStatusesDataViewValues: getShipmentStatusesDataView(state).value,
    language: state.context.session.language,
    locations: state.greif.components.locationFilters.locations,
});

const mapDispatchToProps = {
    updateSearchFields,
};

export interface OrderHistorySearchTagsStyles {
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    tagWrapper?: TagWrapperProps;
}

export const shipmentHistorySearchTagsStyles: OrderHistorySearchTagsStyles = {
    tagWrapper: tableFilterProps.tagWrapper,
};

const styles = shipmentHistorySearchTagsStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ShipmentHistorySearchTags = ({
    parameter,
    shipmentStatusesDataViewValues,
    updateSearchFields,
    language,
    locations,
}: Props) => {
    const [location, setLocation] = useState<string>();

    useEffect(() => {
        const loc = locations.filter(x => x.location === parameter.location);
        setLocation(loc[0]?.locationDescription);
    }, [locations, parameter.location]);

    const fieldRemoveHandler = (fieldName: string, value = "") => {
        updateSearchFields({ [fieldName]: value });
    };

    const removeStatusHandler = (fieldName: string, statusName: string) => {
        const _deliveryStatuses = [...(parameter?.deliveryStatuses || [])];
        const _statusIndex = _deliveryStatuses?.indexOf(statusName);

        if (_statusIndex === -1) {
            return;
        }

        _deliveryStatuses?.splice(_statusIndex, 1);
        _deliveryStatuses.length === 0
            ? fieldRemoveHandler(fieldName)
            : updateSearchFields({ [fieldName]: _deliveryStatuses });
    };

    const getStatusDisplay = (status: string) => {
        return shipmentStatusesDataViewValues?.find(x => x.name === status)?.description || translate("N/A");
    };

    return (
        <TagWrapper {...styles.tagWrapper}>
            {parameter.greifOrderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("greifOrderNumber");
                    }}
                    data-test-selector="shipmentHistory_greifOrderTag"
                >
                    {`${translate("{0}:", translate("Greif Order #"))} ${parameter.greifOrderNumber}`}
                </Tag>
            )}
            {parameter.purchaseOrderNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("purchaseOrderNumber");
                    }}
                    data-test-selector="shipmentHistory_purchaseOrderTag"
                >
                    {`${translate("{0}:", translate("PO #"))} ${parameter.purchaseOrderNumber}`}
                </Tag>
            )}
            {parameter.customerSequence && parameter.customerSequence !== "-1" && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("customerSequence", "-1");
                    }}
                    data-test-selector="shipmentHistory_shipToTag"
                >
                    {`${translate("{0}:", translate("Ship To"))} ${parameter.customerSequence}`}
                </Tag>
            )}
            {parameter.itemNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    data-test-selector="shipmentHistory_itemNumberTag"
                    onDelete={() => {
                        fieldRemoveHandler("itemNumber");
                    }}
                >
                    {`${translate("{0}:", translate("Item #"))} ${parameter.itemNumber}`}
                </Tag>
            )}
            {parameter.deliveryStatuses &&
                parameter.deliveryStatuses.map(status => (
                    <Tag
                        key={status}
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            removeStatusHandler("deliveryStatuses", status);
                        }}
                        data-test-selector="shipmentHistory_statusTag"
                    >
                        {`${translate("{0}:", translate("Status"))} ${getStatusDisplay(status)}`}
                    </Tag>
                ))}
            {parameter.fromDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("fromDate");
                    }}
                    data-test-selector="shipmentHistory_deliveryDateFromTag"
                >
                    {`${translate("{0}:", "Delivery Date From")} ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(parameter.fromDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {parameter.toDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("toDate");
                    }}
                    data-test-selector="shipmentHistory_deliveryDateToTag"
                >
                    {`${translate("{0}:", "Delivery Date To")} ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(parameter.toDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {parameter.isNextDaysFilter && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("isNextDaysFilter");
                    }}
                >
                    {translate("Next 7 Days Shipments")}
                </Tag>
            )}
            {parameter.location && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fieldRemoveHandler("location");
                    }}
                >
                    {`${translate("{0}:", translate("Greif Location"))} ${location}`}
                </Tag>
            )}
        </TagWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ShipmentHistorySearchTags),
    definition: {
        group: constants.widgetGroup.shipmentHistory,
        displayName: "Tags",
        allowedContexts: [constants.pageType.shipmentHistory],
    },
};

export default widgetModule;
